import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CollectionBuildDenimTile,
  CollectionBuildResultDetails,
  ColorPaletteOption,
  PreferenceOption,
  capsulePaletteDisplayName,
  collectionBuildDividerClassName,
  isCapsuleStartingDirection,
  orderPaletteDotsMajorFirst,
  paletteSwatchTitle,
} from "./collectionBuildConstants";
import { trendDisplayName } from "../../constants/imageGeneratorDictionaries";
import useGenerativeResultPreviews from "./shared/useGenerativeResultPreviews";

type Props = {
  details: CollectionBuildResultDetails;
  onCollapse: () => void;
};

/** Session outputs use { stem, url }; snapshots use PreferenceOption. */
type LoosePreference = PreferenceOption & {
  stem?: string;
  url?: string;
};

const DEFAULT_WIDTH = 174;
const MIN_WIDTH = 174;
const MAX_WIDTH = 420;
const MAX_PARENT_RATIO = 0.45;
const WIDTH_STORAGE_KEY = "collection-build-design-details-width";

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const readStoredWidth = (): number => {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (raw == null) return DEFAULT_WIDTH;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return DEFAULT_WIDTH;
    return clamp(Math.round(parsed), MIN_WIDTH, MAX_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const isGenericPaletteLabel = (label?: string) => {
  const trimmed = String(label || "").trim();
  return !trimmed || /^color palettes?$/i.test(trimmed);
};

/** Catalog capsule `name` already stored on the result. Empty when not a capsule. */
const capsuleThemeNameFromDetails = (
  details: CollectionBuildResultDetails
): { direction: string; themeId: string; name: string } => {
  const persist = (details.persistPayload || {}) as {
    startingDirection?: string;
    macroThemeId?: string;
    themeLabel?: string;
  };
  const direction = [details.startingDirection, persist.startingDirection].find(
    value => isCapsuleStartingDirection(value)
  );
  if (!direction) {
    return { direction: "", themeId: "", name: "" };
  }
  const themeId = String(persist.macroThemeId || "").trim();
  const fromDetails = String(details.macroTheme || "").trim();
  const fromLabel = String(persist.themeLabel || "").trim();
  const looksLikeId = (value: string) =>
    !value || value === themeId || value.includes("|");
  let name = fromDetails;
  if (looksLikeId(fromDetails) && !looksLikeId(fromLabel)) {
    name = fromLabel;
  }
  return { direction, themeId, name };
};

/** One chip per colour story. Flattened paletteHexes only if groups are missing. */
const paletteGroupsFor = (
  details: CollectionBuildResultDetails
): ColorPaletteOption[] => {
  const grouped = (details.palettes || []).filter(
    palette => Array.isArray(palette.colors) && palette.colors.length > 0
  );
  if (grouped.length > 0) {
    return grouped;
  }
  if ((details.paletteHexes || []).length > 0) {
    return [
      {
        id: "palette",
        label: "Color Palette",
        colors: details.paletteHexes,
      },
    ];
  }
  return [];
};

const preferenceImageUrl = (item: LoosePreference): string =>
  String(item.imageUrl || item.url || "").trim();

const preferenceStem = (item: LoosePreference): string =>
  String(item.id || item.stem || item.label || "").trim();

/**
 * Preference hover: pattern tiles use the micro-theme already on the snapshot.
 * Surface slides use that slide's name when it is a real name for this id.
 */
const preferenceHoverLabel = (
  assetId: string,
  details: CollectionBuildResultDetails
): string => {
  const id = String(assetId || "").trim();
  const surface = (details.surfaceTrends || []).find(
    item => String(item.id || "").trim() === id
  );
  const slideName = trendDisplayName(surface?.name);
  if (slideName && slideName !== id) {
    return slideName;
  }

  const micro = trendDisplayName(details.microTheme);
  if (micro) {
    return micro;
  }

  const fromThemes = (details.microThemes || [])
    .map(name => trendDisplayName(name))
    .filter(Boolean);
  if (fromThemes.length) {
    return fromThemes.join(", ");
  }

  const microRow = (details.frozenRows || []).find(row =>
    /^micro-themes?$/i.test(row.label)
  );
  const fromRow = trendDisplayName(microRow?.value);
  if (fromRow) {
    return fromRow;
  }

  return id;
};

const isDirectPreviewSrc = (raw: string) =>
  raw.startsWith("blob:") ||
  raw.startsWith("data:") ||
  raw.startsWith("http://") ||
  raw.startsWith("https://");

const isDenimDetails = (details: CollectionBuildResultDetails) =>
  /denim/i.test(details.startingDirection || "");

/** Design-sheet / details Category drops the standalone RTW token only. */
const categoryRowValue = (value: string) =>
  value
    .replace(/(^|\s+)rtw(?=\s|$)/gi, " ")
    .replace(/\s+/g, " ")
    .trim() || value;

/** Per-look Select Product fields — omitted from Design Details (too verbose). */
const LOOK_DETAIL_ROW_LABELS = new Set([
  "Product type",
  "Style type",
  "Influence",
  "Materials",
  "Sleeve",
  "Neckline",
  "Fit",
  "Length",
  "Pattern Format",
  "Embroidery",
  "Silhouette",
]);

type DetailImageTile = {
  previewId: string;
  label: string;
  heading: string;
  imageUrl: string | null;
};

const denimTilesForKind = (
  kind: "material" | "wash",
  heading: string,
  tiles: CollectionBuildDenimTile[] | undefined,
  legacy?: CollectionBuildDenimTile
): DetailImageTile[] => {
  const list = tiles && tiles.length ? tiles : legacy?.id ? [legacy] : [];
  const seen = new Set<string>();
  return list
    .filter(tile => {
      const id = String(tile?.id || "").trim();
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    })
    .map((tile, index) => ({
      previewId: `denim-${kind}-${tile.id}-${index}`,
      label: String(tile.label || tile.id || heading),
      heading,
      imageUrl: String(tile.imageUrl || "").trim() || null,
    }));
};

const CollectionBuildDesignDetails: React.FC<Props> = ({
  details,
  onCollapse,
}) => {
  const paletteGroups = paletteGroupsFor(details);
  const capsuleTheme = capsuleThemeNameFromDetails(details);
  const isDenim = isDenimDetails(details);
  const asideRef = useRef<HTMLElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
  } | null>(null);
  const [width, setWidth] = useState(readStoredWidth);
  const [dragging, setDragging] = useState(false);
  const [lightbox, setLightbox] = useState<{
    src: string;
    label: string;
  } | null>(null);

  // Dedupe by stem (session outputs often repeat the same preference list per
  // moodboard/look). Prefer the entry that already has a url. Index-prefixed
  // preview ids keep React keys / the preview map unique.
  const preferenceEntries = useMemo(() => {
    if (isDenim) {
      return [];
    }
    const byStem = new Map<
      string,
      { stem: string; label: string; imageUrl: string | null; order: number }
    >();
    (details.preferences || []).forEach((item, index) => {
      const loose = item as LoosePreference;
      const stem = preferenceStem(loose);
      const key = stem || `__idx-${index}`;
      const imageUrl = preferenceImageUrl(loose) || null;
      const label = String(item.label || stem || `Preference ${index + 1}`);
      const existing = byStem.get(key);
      if (!existing) {
        byStem.set(key, { stem: key, label, imageUrl, order: index });
        return;
      }
      if (!existing.imageUrl && imageUrl) {
        byStem.set(key, { ...existing, label, imageUrl });
      }
    });
    return Array.from(byStem.values())
      .sort((a, b) => a.order - b.order)
      .map((item, index) => ({
        previewId: `pref-${index}-${item.stem}`,
        label: item.label,
        imageUrl: item.imageUrl,
      }));
  }, [details.preferences, isDenim]);
  const denimMaterialEntries = useMemo(
    () =>
      isDenim
        ? denimTilesForKind(
            "material",
            (details.denimMaterialTiles || []).length > 1
              ? "Materials"
              : "Material",
            details.denimMaterialTiles,
            details.denimMaterialTile
          )
        : [],
    [details.denimMaterialTile, details.denimMaterialTiles, isDenim]
  );
  const denimWashEntries = useMemo(
    () =>
      isDenim
        ? denimTilesForKind(
            "wash",
            (details.denimWashTiles || []).length > 1 ? "Washes" : "Wash",
            details.denimWashTiles,
            details.denimWashTile
          )
        : [],
    [details.denimWashTile, details.denimWashTiles, isDenim]
  );
  const denimEntries = useMemo(
    () => [...denimMaterialEntries, ...denimWashEntries],
    [denimMaterialEntries, denimWashEntries]
  );
  const preferencePreviewItems = useMemo(
    () => [
      ...preferenceEntries.map(item => ({
        id: item.previewId,
        imageUrl: item.imageUrl,
      })),
      ...denimEntries.map(item => ({
        id: item.previewId,
        imageUrl: item.imageUrl,
      })),
    ],
    [preferenceEntries, denimEntries]
  );
  const preferencePreviews = useGenerativeResultPreviews(
    preferencePreviewItems
  );

  useEffect(() => {
    if (!lightbox) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLightbox(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightbox]);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
    } catch {
      // ignore quota / private mode
    }
  }, [width]);

  const maxWidthForLayout = () => {
    const grid = asideRef.current?.parentElement?.parentElement;
    const fromParent = grid
      ? Math.floor(grid.clientWidth * MAX_PARENT_RATIO)
      : MAX_WIDTH;
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, fromParent));
  };

  const onHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
    };
    setDragging(true);
  };

  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = drag.startWidth + (event.clientX - drag.startX);
    setWidth(clamp(Math.round(next), MIN_WIDTH, maxWidthForLayout()));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const previewSrcFor = (previewId: string, imageUrl: string | null) => {
    const resolved = preferencePreviews[previewId];
    const raw = imageUrl || "";
    return resolved || (isDirectPreviewSrc(raw) ? raw : "");
  };

  return (
    <>
      <aside
        ref={asideRef}
        style={{ width }}
        className="relative flex h-full min-h-0 w-full shrink-0 flex-col self-stretch overflow-hidden rounded-2xl border border-light bg-white pb-2"
      >
        <div
          className={`flex shrink-0 items-center justify-between gap-1 border-b ${collectionBuildDividerClassName} px-3 py-2.5`}
        >
          <h3 className="min-w-0 truncate text-sm font-semibold text-secondary">
            Design Details
          </h3>
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse design details"
            className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-tertiary hover:bg-lightGray hover:text-secondary focus:outline-none"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M7.5 2.5 4 6l3.5 3.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2.5">
          <dl className="space-y-2.5">
            {details.frozenRows
              .filter(row => {
                if (LOOK_DETAIL_ROW_LABELS.has(row.label)) {
                  return false;
                }
                if ((details.microThemes || []).length > 1) {
                  return (
                    row.label !== "Micro-theme" && row.label !== "Micro-themes"
                  );
                }
                return true;
              })
              .map((row, rowIndex) => {
                const value =
                  row.label === "Category"
                    ? categoryRowValue(row.value)
                    : row.value;
                return (
                  <React.Fragment key={`${row.label}-${rowIndex}`}>
                    <div className="flex items-start justify-between gap-x-2 text-xs leading-snug text-secondary">
                      <dt
                        className="max-w-[40%] shrink-0 whitespace-normal break-normal"
                        title={row.label}
                      >
                        {row.label}
                      </dt>
                      <dd
                        className="min-w-0 flex-1 whitespace-normal break-normal text-right font-medium [overflow-wrap:normal] [word-break:normal]"
                        title={value}
                      >
                        {value.replace(/-/g, "\u2011")}
                      </dd>
                    </div>
                    {row.label === "Range Plan" &&
                    details.rangePlanBreakdown.length > 0 ? (
                      <div className="rounded-md bg-lightGray px-2 py-1.5">
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                          {details.rangePlanBreakdown.map(item => {
                            const text = `${item.quantity} ${item.productType}`;
                            return (
                              <p
                                key={item.productType}
                                className="min-w-0 whitespace-normal break-normal text-[11px] leading-snug text-secondary [overflow-wrap:normal] [word-break:normal]"
                                title={text}
                              >
                                {text}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })}
          </dl>

          {(details.microThemes || []).length > 1 ? (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-secondary">
                Micro-themes
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(details.microThemes || []).map(name => (
                  <span
                    key={name}
                    className="max-w-full truncate rounded-full border border-light bg-white px-2.5 py-1 text-[10px] leading-tight text-secondary"
                    title={name}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {paletteGroups.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-secondary">
                {paletteGroups.length > 1 ? "Color Palettes" : "Color Palette"}
              </p>
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
                {paletteGroups.map((palette, paletteIndex) => {
                  const capsuleLabel = capsulePaletteDisplayName({
                    startingDirection: capsuleTheme.direction,
                    capsuleName: capsuleTheme.name,
                    paletteId: palette.id,
                    themeId: capsuleTheme.themeId,
                  });
                  const caption = capsuleLabel || palette.label;
                  const showLabel = capsuleLabel
                    ? true
                    : !isGenericPaletteLabel(palette.label);
                  const ordered = orderPaletteDotsMajorFirst(
                    palette.colors,
                    palette.swatches
                  );
                  return (
                    <div
                      key={palette.id || `palette-${paletteIndex}`}
                      className="flex w-fit min-w-0 max-w-full flex-col"
                    >
                      <div className="inline-flex w-fit max-w-full items-center gap-2 overflow-x-auto rounded-full border border-light bg-white px-2.5 py-1.5">
                        {ordered.colors.map((hex, index) => (
                          <span
                            key={`${palette.id}-${hex}-${index}`}
                            className="h-5 w-5 shrink-0 rounded-full border border-black/10"
                            style={{ backgroundColor: hex }}
                            title={
                              capsuleLabel ||
                              paletteSwatchTitle(hex, ordered.swatches?.[index])
                            }
                          />
                        ))}
                      </div>
                      {showLabel ? (
                        <p
                          className="mt-1 max-w-full truncate text-[10px] leading-tight text-secondary"
                          title={caption}
                        >
                          {caption}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {denimMaterialEntries.length > 0 || denimWashEntries.length > 0 ? (
            <div className="mt-4 flex flex-col gap-3">
              {[
                {
                  key: "materials",
                  heading: denimMaterialEntries[0]?.heading || "Material",
                  entries: denimMaterialEntries,
                },
                {
                  key: "washes",
                  heading: denimWashEntries[0]?.heading || "Wash",
                  entries: denimWashEntries,
                },
              ]
                .filter(group => group.entries.length > 0)
                .map(group => (
                  <div key={group.key}>
                    <p className="mb-2 text-xs font-medium text-secondary">
                      {group.heading}
                    </p>
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
                      {group.entries.map(item => {
                        const previewSrc = previewSrcFor(
                          item.previewId,
                          item.imageUrl
                        );
                        return (
                          <div
                            key={item.previewId}
                            className="flex w-fit min-w-0 max-w-full flex-col"
                          >
                            <button
                              type="button"
                              disabled={!previewSrc}
                              onClick={() => {
                                if (!previewSrc) {
                                  return;
                                }
                                setLightbox({
                                  src: previewSrc,
                                  label: item.label,
                                });
                              }}
                              className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-lightGray focus:outline-none enabled:cursor-zoom-in enabled:hover:ring-1 enabled:hover:ring-secondary/40 disabled:cursor-default"
                              title={item.label}
                              aria-label={`View ${item.label}`}
                            >
                              {previewSrc ? (
                                <img
                                  src={previewSrc}
                                  alt={item.label}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full bg-inactive" />
                              )}
                            </button>
                            <p
                              className="mt-1 max-w-[4.5rem] truncate text-[10px] leading-tight text-secondary"
                              title={item.label}
                            >
                              {item.label}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          ) : null}

          {!isDenim && preferenceEntries.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-secondary">
                Preferences
              </p>
              <div className="flex flex-wrap gap-2">
                {preferenceEntries.map(item => {
                  const previewSrc = previewSrcFor(
                    item.previewId,
                    item.imageUrl
                  );
                  const hoverLabel = preferenceHoverLabel(item.label, details);
                  return (
                    <button
                      key={item.previewId}
                      type="button"
                      disabled={!previewSrc}
                      onClick={() => {
                        if (!previewSrc) {
                          return;
                        }
                        setLightbox({ src: previewSrc, label: item.label });
                      }}
                      className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-lightGray focus:outline-none enabled:cursor-zoom-in enabled:hover:ring-1 enabled:hover:ring-secondary/40 disabled:cursor-default"
                      title={hoverLabel}
                      aria-label={`View ${hoverLabel}`}
                    >
                      {previewSrc ? (
                        <img
                          src={previewSrc}
                          alt={item.label}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full bg-inactive" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize design details"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={event => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              setWidth(w => clamp(w - 8, MIN_WIDTH, maxWidthForLayout()));
            } else if (event.key === "ArrowRight") {
              event.preventDefault();
              setWidth(w => clamp(w + 8, MIN_WIDTH, maxWidthForLayout()));
            }
          }}
          className="group absolute inset-y-0 right-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center touch-none"
        >
          <span
            className={`h-8 w-px rounded-full transition-colors ${
              dragging
                ? "bg-secondary/45"
                : "bg-tertiary/25 group-hover:bg-tertiary/40 group-focus-visible:bg-tertiary/40"
            }`}
          />
        </div>
      </aside>
      {lightbox
        ? createPortal(
            <div
              className="fixed inset-0 z-[80] flex items-center justify-center font-avenir"
              role="dialog"
              aria-modal="true"
              aria-label="Preference preview"
            >
              <div
                className="fixed inset-0 bg-black/70"
                onClick={() => setLightbox(null)}
              />
              <button
                type="button"
                onClick={() => setLightbox(null)}
                aria-label="Close preference preview"
                className="absolute right-6 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-secondary hover:bg-lightGray focus:outline-none"
              >
                ×
              </button>
              <div className="relative z-10 max-h-[90vh] max-w-[90vw]">
                <img
                  src={lightbox.src}
                  alt={lightbox.label}
                  className="max-h-[90vh] max-w-[90vw] rounded-2xl bg-[#F7F3EE] object-contain shadow-lg"
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default CollectionBuildDesignDetails;
